/**
 * Soft≠Live Page90 — Auth Login / Signup / Forgot / SignOut Live desks.
 * (Page38 Live buses retained — residual bare Sign in / recovery CTAs upgraded.)
 * Soft residual deleted:
 *  - unlabeled Sign in / recovery / resend CTAs
 *  - Soft invent reset paths without Live enabled probes
 *  - Soft approve invent on signup waiting
 */
import { useState, useEffect, useRef } from "react";
import { AnimatePresence } from "framer-motion";
import { html, M, F, Ico as I, Input, Field, Alert, Steps, SOFT, FADE, go } from "./ui.js";
import * as D from "./data.js";
import { Overlay, SIGN_IN, SIGN_UP, FORGOT_SEND, FORGOT_SAVE } from "./loading.js";
import { api, path } from "./api.js";
import { useSession } from "./session.js";

const FootLink = ({ to, children }) => html`
  <a href=${"#" + to} onClick=${e=>{ e.preventDefault(); go(to); }}>${children}</a>`;

/** Where a signed-in principal lands — site truth first (Operations). */
function homeFor(_me){
  return "/ops";
}

const Brand = () => html`
  <div className="auth__brand">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 2.6 20.4 6v6.2c0 5-3.5 8.8-8.4 10.4C7.1 21 3.6 17.2 3.6 12.2V6z"
        stroke="var(--aqua)" strokeWidth="1.4" strokeLinejoin="round"/>
      <path d="M12 8v8M8.4 11.2 12 8l3.6 3.2" stroke="var(--aqua)" strokeWidth="1.4"
        strokeLinecap="round" strokeLinejoin="round" opacity=".62"/>
    </svg>
    <div>
      <b>SENTINEL</b>
      <em style=${{ display:"block", fontStyle:"normal", fontSize:"var(--t-micro)",
        letterSpacing:".14em", color:"var(--fg-4)", marginTop:2 }}>ESASECURE</em>
    </div>
  </div>`;

const Shell = ({ wide, children }) => html`
  <div className="sheet auth auth--liquid desk-craft house-rack" data-cmc-page="page90-auth" data-cmc-auth="page38" data-cmc-found="found-2" data-viewport-fill="1">
    <${M.div} className=${"auth__card glass"+(wide?" auth__card--wide":"")}
        initial=${{ opacity:0, y:20, scale:.985 }} animate=${{ opacity:1, y:0, scale:1 }} transition=${SOFT}>
      <div style=${{ position:"relative", zIndex:1 }}>${children}</div>
    <//>
  </div>`;

/* ═══════════════════ LOGIN ═══════════════════════════════════════════════ */
const LOGIN_PATH  = path("post__auth_login");
const SECOND_PATH = path("post__auth_login_email-second");
const RESEND_PATH = path("post__auth_code_resend");

/** Map transport failures to human copy — never paint raw backend detail strings. */
function loginMessage(e){
  const m = String((e && e.message) || e || "");
  if(
    m.startsWith("401") ||
    /invalid credentials/i.test(m) ||
    /session ended/i.test(m)
  ) {
    return { t:"That did not match", s:"The username or password is incorrect. Nothing about your account has changed." };
  }
  if(
    m.startsWith("403") ||
    /closed|suspended|not.?approved|forbidden|disabled|awaiting|not activated/i.test(m)
  ) {
    return { t:"This account cannot sign in here", s:"The account is closed, suspended, or not yet approved." };
  }
  if(m.startsWith("429") || /too many/i.test(m)) {
    return { t:"Too many attempts", s:"Please wait a moment, then try again." };
  }
  if(m.startsWith("503") || /unavailable/i.test(m)) {
    return { t:"Sign-in is briefly unavailable", s:"The service that checks credentials is not answering. Try again shortly." };
  }
  if(m.startsWith("400") || /validation|required/i.test(m)) {
    return { t:"Check your details", s:"Username and password are required." };
  }
  if(/timed out|network|failed to fetch|unreachable/i.test(m)) {
    return { t:"Cannot reach sign-in", s:"The browser could not reach the login service. Check your network and try again." };
  }
  return { t:"Sign-in did not complete", s:"Please try again. If this continues, contact your administrator." };
}

function signupMessage(e){
  const m = String((e && e.message) || e || "");
  if(m.startsWith("409") || /exists|taken|duplicate/i.test(m)) {
    return { t:"Account already exists", s:"That username or email is already registered. Try signing in or reset the password." };
  }
  if(m.startsWith("400") || /invalid|validation/i.test(m)) {
    return { t:"Check your details", s:"Username, email, phone, and a strong password are required." };
  }
  if(m.startsWith("429")) {
    return { t:"Too many attempts", s:"Please wait a moment before trying again." };
  }
  if(m.startsWith("503")) {
    return { t:"Registration is briefly unavailable", s:"Nothing was saved. Please try again shortly." };
  }
  return { t:"Account was not created", s:"Please try again. If this continues, contact your administrator." };
}

export function Login({ toast }){
  const { reload } = useSession();
  const [u,setU] = useState("");
  const [p,setP] = useState("");
  const [show,setShow] = useState(false);
  const [phase,setPhase] = useState("form");   // form | second | locked
  const [busy,setBusy] = useState(false);
  const [err,setErr] = useState(null);
  const [left,setLeft] = useState(120);
  const [second,setSecond] = useState(null);   // server's two-step envelope
  const pending = useRef(null);

  useEffect(()=>{
    if(phase!=="locked") return;
    const id = setInterval(()=> setLeft(s=> s>0 ? s-1 : 0), 1000);
    return ()=>clearInterval(id);
  },[phase]);

  /* The request leaves immediately; the step animation runs alongside it. The
     screen settles when both are done, so the wait is never shorter than the
     truth and never longer than it has to be.

     Read username/password from FormData (and native DOM) so browser
     autofill that never fired React onChange still reaches POST /auth/login. */
  const submit = e => {
    e.preventDefault();
    setErr(null);
    const form = e.currentTarget;
    let user = u.trim();
    let pass = p;
    try {
      const fd = new FormData(form);
      const fu = String(fd.get("username") || "").trim();
      const fp = String(fd.get("password") || "");
      if (fu) user = fu;
      if (fp) pass = fp;
    } catch (_e) { /* FormData unavailable — state only */ }
    try {
      const uEl = form && form.querySelector ? form.querySelector('input[name="username"],input[autocomplete="username"]') : null;
      const pEl = form && form.querySelector ? form.querySelector('input[name="password"],input[autocomplete="current-password"]') : null;
      const du = uEl && String(uEl.value || "").trim();
      const dp = pEl && String(pEl.value || "");
      if (du) user = du;
      if (dp) pass = dp;
    } catch (_e) { /* optional DOM mirror */ }
    if (user !== u.trim()) setU(user);
    if (pass !== p) setP(pass);
    if(!user){ setErr({ t:"Enter your username", s:"Nothing was checked yet — the field is empty." }); return; }
    if(!pass){ setErr({ t:"Enter your password", s:"Nothing was checked yet — the field is empty." }); return; }
    pending.current = api(LOGIN_PATH, {
      method:"POST",
      body:{ username:user, password:pass },
      skipExpire: true,
      timeoutMs: 8000,
      retries: 0,
    })
      .then(r => ({ ok:true, r }))
      .catch(e => ({ ok:false, e }));
    setBusy(true);
  };

  const settle = async () => {
    try {
      const out = await pending.current;
      pending.current = null;
      if(!out) return;

      if(!out.ok){
        const m = String((out.e && out.e.message) || out.e);
        // 429 is a lockout, not a failure. The server tells us how long.
        const wait = /^429/.test(m) ? (Number((m.match(/(\d+)\s*second/i)||[])[1]) || 120) : 0;
        if(wait){ setLeft(wait); setPhase("locked"); return; }
        setErr(loginMessage(out.e));
        setP("");
        return;
      }

      // Prefer server CSRF immediately (do not wait only on cookie warm).
      try {
        if (out.r && out.r.csrf_token) {
          const { setCsrfToken } = await import("../lib/csrf.js");
          setCsrfToken(String(out.r.csrf_token));
        }
      } catch (_e) { /* optional */ }

      if(out.r && out.r.requires_email_second){
        setSecond(out.r);
        setPhase("second");
        toast("A six-digit code was emailed","warn");
        return;
      }
      // Session cookie is set; warm CSRF then establish me. App effect routes
      // /auth → home once `me` is live — do not race go() against a null probe.
      await Promise.race([
        warmAfterLogin(),
        new Promise((_, rej) => setTimeout(() => rej(new Error("csrf_warm_timeout")), 8000)),
      ]).catch(() => {});
      let me = null;
      for (let i = 0; i < 2; i++) {
        me = await Promise.race([
          reload(),
          new Promise((_, rej) => setTimeout(() => rej(new Error("me_probe_timeout")), 8000)),
        ]).catch(() => null);
        if (me) break;
        await new Promise((r) => setTimeout(r, 80 * (i + 1)));
      }
      if (me) {
        toast(`Signed in as ${me.username}`);
        return;
      }
      toast("Signed in — opening the console", "warn");
      try { location.reload(); } catch (_e) { go(homeFor(me)); }
    } finally {
      setBusy(false);
    }
  };

  async function warmAfterLogin() {
    try {
      const { refreshCsrfToken, setCsrfToken, _readCsrfCookie } = await import("../lib/csrf.js");
      const ck = _readCsrfCookie();
      if (ck) setCsrfToken(ck);
      try { await refreshCsrfToken(); } catch (_e) { /* reload will retry */ }
    } catch (_e) { /* csrf module optional at settle */ }
  }

  if(phase === "locked") return html`
    <${Shell}>
      <${Brand}/>
      <h1>Too many attempts</h1>
      <p className="lede">This form slows you down after too many tries. Nothing is wrong with your account — the lock clears on its own.</p>
      <div className="flat" style=${{ padding:"18px 20px", textAlign:"center", marginBottom:20 }}>
        <div style=${{ fontFamily:"var(--mono)", fontSize:"var(--t-display)", fontWeight:500, letterSpacing:"-.03em", color:"var(--amber)" }}>
          ${String(Math.floor(left/60)).padStart(2,"0")}:${String(left%60).padStart(2,"0")}</div>
        <div style=${{ fontFamily:"var(--mono)", fontSize:"var(--t-label)", color:"var(--fg-4)", marginTop:6 }}>Seconds left · ${left}</div>
      </div>
      <button type="button" className="btn btn--ghost btn--wide" disabled=${left>0} onClick=${()=>{setPhase("form");setErr(null);}}>
        ${left>0 ? "Wait for the lock to clear" : "Try again"}</button>
      <div className="auth__foot"><${FootLink} to="/auth/forgot">Reset password</${FootLink}></div>
    <//>`;

  if(phase === "second") return html`
    <${Shell}>
      <${Brand}/>
      <h1>One more step</h1>
      <p className="lede">Platform staff confirm sign-in with a second code. A six-digit code went to the address on your account${second && second.ttl_seconds ? ` — it is valid for ${Math.round(second.ttl_seconds/60)} minutes` : ""}.</p>
      ${err && html`<${Alert} title=${err.t} say=${err.s}/>`}
      <${OTP} onDone=${async code => {
        if(busy) return;
        setBusy(true); setErr(null);
        try {
          await api(SECOND_PATH, {
            method:"POST",
            body:{ username:(second&&second.username)||u.trim(), code },
            skipExpire: true,
            timeoutMs: 8000,
            retries: 0,
          });
          await Promise.race([
            warmAfterLogin(),
            new Promise((_, rej) => setTimeout(() => rej(new Error("csrf_warm_timeout")), 8000)),
          ]).catch(() => {});
          let me = null;
          for (let i = 0; i < 2; i++) {
            me = await Promise.race([
              reload(),
              new Promise((_, rej) => setTimeout(() => rej(new Error("me_probe_timeout")), 8000)),
            ]).catch(() => null);
            if (me) break;
            await new Promise((r) => setTimeout(r, 80 * (i + 1)));
          }
          if (me) { toast(`Signed in as ${me.username}`); return; }
          toast("Signed in — opening the console", "warn");
          try { location.reload(); } catch (_e) { go(homeFor(me)); }
        } catch(_e){
          setErr({ t:"That code was not accepted", s:"It may have expired or already been used. Request a new code." });
        } finally { setBusy(false); }
      }}/>
      <div className="auth__foot">
        <button type="button" disabled=${busy} onClick=${async ()=>{
          try {
            await api(RESEND_PATH, { method:"POST", body:{ username:(second&&second.username)||u.trim() } });
            toast("A new code was sent","warn");
          } catch(_e){ toast("Could not resend the code","bad"); }
        }}>Send another code</button>
        <button type="button" onClick=${()=>{ setPhase("form"); setSecond(null); setErr(null); }}>Use a different account</button>
      </div>
      <${Overlay} show=${busy} title="Confirming sign-in"
        say="Verifying the six-digit code and opening your session."
        steps=${SIGN_IN} onDone=${()=>{}}/>
    <//>`;

  return html`
    <${Shell}>
      <${Brand}/>
      <h1>Sign in</h1>
      <p className="lede">Your session lasts eight hours. If an administrator changes your role or password, every device signs out at once.</p>
      ${err && html`<${Alert} title=${err.t} say=${err.s}/>`}
      <form onSubmit=${submit}>
        <${Field} label="Username">
          <${Input} icon="user" name="username" value=${u} onChange=${e=>setU(e.target.value)}
            placeholder="2–64 characters" autoComplete="username"/>
        <//>
        <${Field} label="Password">
          <${Input} icon="lock" name="password" type=${show?"text":"password"} value=${p} onChange=${e=>setP(e.target.value)}
            placeholder="••••••••••••" autoComplete="current-password"
            act=${html`<${I} d=${show?"eyeOff":"eye"} w=${15}/>`} onAct=${()=>setShow(s=>!s)}/>
        <//>
        <button type="submit" className="btn btn--go btn--wide" style=${{ marginTop:6 }} disabled=${busy}>
          ${busy ? html`<${F}><span className="think"><i/><i/><i/></span> Checking…<//>` : "Sign in"}
        </button>
      </form>
      <div className="auth__foot">
        <${FootLink} to="/auth/forgot">Forgot password</${FootLink}>
        <${FootLink} to="/auth/signup">Create account</${FootLink}>
      </div>
      <${Overlay} show=${busy} title="Signing you in"
        say="Verifying your credentials securely."
        steps=${SIGN_IN} onDone=${settle}/>
    <//>`;
}

/* ═══════════════════ OTP ═════════════════════════════════════════════════
   Signup + password-reset email use product 6-digit numeric codes.
   Strip spaces/dashes/NBSP from paste so mail clients that insert separators
   still fill the boxes. Alphanumeric SHA tokens are no longer issued. */
function OTP({ onDone, len = 6 }){
  const [v,setV] = useState(Array(len).fill(""));
  const refs = useRef([]);
  const digitsOnly = s => String(s||"").replace(/[\u00a0\s\-_.]/g,"").replace(/\D/g,"");
  const set = (i,val) => {
    const c = digitsOnly(val).slice(-1);
    const next = [...v]; next[i] = c; setV(next);
    if(c && i < len-1) refs.current[i+1]?.focus();
    if(next.every(Boolean)) setTimeout(()=>onDone(next.join("")), 420);
  };
  const key = (i,e) => {
    if(e.key === "Backspace" && !v[i] && i > 0) refs.current[i-1]?.focus();
    if(e.key === "ArrowLeft" && i > 0) refs.current[i-1]?.focus();
    if(e.key === "ArrowRight" && i < len-1) refs.current[i+1]?.focus();
  };
  const paste = e => {
    const d = digitsOnly(e.clipboardData.getData("text")||"").slice(0,len);
    if(!d) return; e.preventDefault();
    const next = Array(len).fill(""); d.split("").forEach((c,i)=>next[i]=c); setV(next);
    if(d.length === len) setTimeout(()=>onDone(next.join("")), 420); else refs.current[d.length]?.focus();
  };
  useEffect(()=>{ refs.current[0]?.focus(); },[]);
  return html`
    <div className="otp" onPaste=${paste}>
      ${v.map((c,i)=> html`
        <input key=${i} ref=${el=>refs.current[i]=el} value=${c} data-filled=${c?"1":"0"}
          inputMode="numeric" autoComplete="one-time-code" maxLength=${1}
          onChange=${e=>set(i,e.target.value)}
          onKeyDown=${e=>key(i,e)} aria-label=${`Digit ${i+1}`}/>`)}
    </div>`;
}

/* ═══════════════════ SIGN UP ═════════════════════════════════════════════ */
const SIGNUP_START  = path("post__auth_signup_start");
const SIGNUP_VERIFY = path("post__auth_signup_verify");
const SIGNUP_RESEND = path("post__auth_code_resend");
const VERIFY_PHONE  = path("post__auth_verify-phone");

export function Signup({ toast }){
  const [step,setStep] = useState(0);
  const [busy,setBusy] = useState(false);
  const [err,setErr] = useState(null);
  const [f,setF] = useState({ username:"", email:"", phone:"", password:"" });
  const [show,setShow] = useState(false);
  /** Live envelope from POST /auth/signup/start — never Soft-invented. */
  const [startLive,setStartLive] = useState(null);
  /** Live status after verify (awaiting_approval | active | …). */
  const [verifyLive,setVerifyLive] = useState(null);
  const pending = useRef(null);
  const on = k => e => setF(s=>({ ...s, [k]:e.target.value }));
  const rules = D.PW_RULES.map(r=>({ ...r, ok:r.test(f.password) }));
  const pwOk = rules.every(r=>r.ok);

  const start = () => {
    setErr(null);
    if(!pwOk || !f.username.trim() || !f.email.trim() || !f.phone.trim()){
      setErr({ t:"Complete every field", s:"Username, email, phone and a valid password are required before anything is sent." });
      return;
    }
    pending.current = api(SIGNUP_START, {
      method:"POST",
      body:{
        username:f.username.trim(),
        password:f.password,
        email:f.email.trim(),
        phone:f.phone.trim(),
      },
    }).then(r => ({ ok:true, r })).catch(e => ({ ok:false, e }));
    setBusy(true);
  };

  const settleStart = async () => {
    const out = await pending.current;
    pending.current = null;
    setBusy(false);
    if(!out) return;
    if(!out.ok){
      setErr(signupMessage(out.e));
      return;
    }
    setStartLive(out.r || {});
    setStep(1);
    const phoneOk = !!(out.r && out.r.phone_verification_call_initiated);
    toast(phoneOk
      ? "Email code on the way · voice call placed"
      : "Email code on the way · place the voice call to finish", "go");
  };

  const resendEmail = async () => {
    try {
      await api(SIGNUP_RESEND, {
        method:"POST",
        body:{ username:f.username.trim(), channel:"email", purpose:"signup" },
      });
      toast("Email code re-sent · wait before asking again", "warn");
    } catch (_e){ toast("Could not resend the email code", "bad"); }
  };

  const callPhoneAgain = async () => {
    try {
      await api(VERIFY_PHONE, {
        method:"POST",
        body:{ username:f.username.trim() },
      });
      toast("Voice verification call placed", "go");
    } catch (_e){
      toast("Could not place the verification call", "bad");
    }
  };

  const onVerifyCode = async (code) => {
    setErr(null);
    try {
      const out = await api(SIGNUP_VERIFY, {
        method:"POST",
        body:{ username:f.username.trim(), email_code:code },
      });
      setVerifyLive(out || {});
      const st = String((out && out.status) || "");
      if(st === "active"){
        toast("Account is active — you can sign in", "go");
        go("/auth/login");
        return;
      }
      setStep(2);
      toast(st === "awaiting_approval"
        ? "Verified — waiting for administrator approval"
        : "Verified", "go");
    } catch(e){
      const m = String((e && e.message) || e);
      if(/phone not verified/i.test(m)){
        setErr({
          t:"Phone is not verified yet",
          s:"Answer the automated call first, or place another call before submitting the email code.",
        });
        return;
      }
      setErr({ t:"That code was not accepted", s:"It may have expired or already been used. Request a new code." });
    }
  };

  const liveStatus = String((verifyLive && verifyLive.status)
    || (verifyLive && verifyLive.already_verified && verifyLive.status)
    || "awaiting_approval");
  const needsApproval = liveStatus === "awaiting_approval"
    || !!(verifyLive && verifyLive.requires_approval)
    || !!(startLive && startLive.requires_approval !== false);

  return html`
    <${Shell} wide>
      <${Brand}/>
      <${Steps} at=${step} items=${["Details","Verify","Approval"]}/>
      ${err && html`<${Alert} title=${err.t} say=${err.s}/>`}
      <${AnimatePresence} mode="wait">
        ${step===0 && html`
          <${M.div} key="s0" initial=${{ opacity:0, x:14 }} animate=${{ opacity:1, x:0 }}
              exit=${{ opacity:0, x:-14 }} transition=${FADE}>
            <h1>Create an account</h1>
            <p className="lede">Public signup creates a tenant administrator. You will own devices, users and settings for your own organisation — never anyone else's.</p>
            <div className="grid g2" style=${{ gap:"0 16px" }}>
              <${Field} label="Username" hint="2–64 characters of A–Z, 0–9, dot, dash or underscore">
                <${Input} icon="user" value=${f.username} onChange=${on("username")} placeholder="your.name"/>
              <//>
              <${Field} label="Email" hint="A six-digit code goes here">
                <${Input} icon="mail" value=${f.email} onChange=${on("email")} placeholder="you@company.com" type="email"/>
              <//>
            </div>
            <${Field} label="Phone" hint="Required. Verified by an automated voice call, not SMS.">
              <${Input} icon="phone" value=${f.phone} onChange=${on("phone")} placeholder="+60 12 345 6789"/>
            <//>
            <${Field} label="Password">
              <${Input} icon="lock" type=${show?"text":"password"} value=${f.password} onChange=${on("password")}
                placeholder="At least 12 characters"
                act=${html`<${I} d=${show?"eyeOff":"eye"} w=${15}/>`} onAct=${()=>setShow(s=>!s)}/>
            <//>
            <div className="pw-grid">
              ${rules.map(r=> html`
                <span key=${r.id} className="pw-rule" data-ok=${r.ok?"1":"0"}>
                  <${I} d=${r.ok?"check":"minus"} w=${11}/>${r.say}
                </span>`)}
            </div>
            <button type="button" className="btn btn--go btn--wide" style=${{ marginTop:20 }}
                disabled=${!pwOk || !f.username || !f.email || !f.phone || busy}
                onClick=${start}>Continue</button>
            <div className="auth__foot"><${FootLink} to="/auth/login">I already have an account</${FootLink}></div>
          <//>`}

        ${step===1 && html`
          <${M.div} key="s1" initial=${{ opacity:0, x:14 }} animate=${{ opacity:1, x:0 }}
              exit=${{ opacity:0, x:-14 }} transition=${FADE}>
            <h1>Verify both channels</h1>
            <p className="lede">Enter the code from your email. A voice call to <b style=${{ color:"var(--fg-2)" }}>${f.phone||"your phone"}</b> must complete before verification is accepted${
              startLive && startLive.ttl_seconds
                ? (" — codes last about " + Math.round(Number(startLive.ttl_seconds)/60) + " minutes")
                : ""
            }.</p>
            ${startLive && startLive.phone_verification_call_initiated === false
              ? html`<${Alert} tone="warn" title="Voice call not placed yet"
                  say="Place a voice call before entering the email code, or verification will refuse until the phone is confirmed."/>`
              : null}
            <${OTP} onDone=${onVerifyCode}/>
            <div className="auth__foot">
              <button type="button" onClick=${resendEmail}>Resend email code</button>
              <button type="button" onClick=${callPhoneAgain}>Place voice call</button>
              <button type="button" onClick=${()=>{ setStep(0); setErr(null); }}>Change details</button>
            </div>
          <//>`}

        ${step===2 && html`
          <${M.div} key="s2" initial=${{ opacity:0, x:14 }} animate=${{ opacity:1, x:0 }}
              exit=${{ opacity:0, x:-14 }} transition=${FADE} style=${{ textAlign:"center" }}>
            <div style=${{ display:"grid", placeItems:"center", padding:"14px 0 22px" }}>
              <div className="orbit"><i/><i/><i/></div>
            </div>
            <h1>${needsApproval || liveStatus === "awaiting_approval"
              ? "Waiting on approval"
              : "Verification complete"}</h1>
            <p className="lede" style=${{ textAlign:"center", maxWidth:400, marginInline:"auto" }}>
              ${liveStatus === "active"
                ? "Your account is active — you can sign in now."
                : liveStatus === "awaiting_approval" || needsApproval
                  ? "An administrator must release this account before you can sign in."
                  : "Your details were verified. Sign-in may open once an administrator releases the account."}
            </p>
            <div className="flat" style=${{ padding:"14px 16px", textAlign:"left", marginBottom:18 }}>
              <div className="kv"><span>Account</span><b className="hi">${f.username||"—"}</b></div>
              <div className="kv"><span>Status</span><b className="warn">${liveStatus==="awaiting_approval"?"Awaiting approval":liveStatus||"—"}</b></div>
              <div className="kv"><span>Role on release</span><b className="hi">admin</b></div>
            </div>
            <div className="auth__foot" style=${{ justifyContent:"center" }}><${FootLink} to="/auth/login">Back to sign in</${FootLink}></div>
          <//>`}
      <//>
      <${Overlay} show=${busy} title="Creating your account"
        say="Checking your details and starting verification."
        steps=${SIGN_UP}
        onDone=${settleStart}/>
    <//>`;
}

/* ═══════════════════ FORGOT PASSWORD ═════════════════════════════════════ */
export function Forgot({ toast }){
  const [mode,setMode] = useState(null);   // null | email | offline
  const [step,setStep] = useState(0);
  const [busyKind,setBusyKind] = useState(null); // null | send | save | offline
  const [err,setErr] = useState(null);
  const [u,setU] = useState("");
  const [email,setEmail] = useState("");
  const [code,setCode] = useState("");
  const [pw,setPw] = useState("");
  const [pw2,setPw2] = useState("");
  const [show,setShow] = useState(false);
  const [blob,setBlob] = useState("");
  const [plain,setPlain] = useState("");
  const [emailOn,setEmailOn] = useState(null);
  const [offlineOn,setOfflineOn] = useState(null);
  const pending = useRef(null);
  const busy = !!busyKind;
  const rules = D.PW_RULES.map(r=>({ ...r, ok:r.test(pw) }));
  const ok = rules.every(r=>r.ok) && pw === pw2 && pw2.length > 0;

  useEffect(()=>{
    let dead = false;
    (async ()=>{
      try {
        const [e, o] = await Promise.all([
          api(path("get__auth_forgot_email_enabled")).catch(()=>({ enabled:false })),
          api(path("get__auth_forgot_enabled")).catch(()=>({ enabled:false })),
        ]);
        if(!dead){
          setEmailOn(!!(e && e.enabled));
          setOfflineOn(!!(o && o.enabled));
        }
      } catch(_e){
        if(!dead){ setEmailOn(false); setOfflineOn(false); }
      }
    })();
    return ()=>{ dead = true; };
  },[]);

  const settleSend = async () => {
    try {
      const out = await pending.current;
      pending.current = null;
      if(!out) return;
      if(!out.ok){
        const m = String((out.e && out.e.message) || out.e || "");
        setErr({
          t: m.includes("429") || /cooldown/i.test(m) ? "Please wait before sending again" : "Could not start the reset",
          s: m.includes("429") || /cooldown/i.test(m)
            ? "A code was already requested. Wait about a minute, then try again."
            : "Please try again in a moment. If email delivery is offline, contact an administrator.",
        });
        return;
      }
      setStep(1);
      toast("If that pair matches, a code is on its way", "go");
    } finally {
      setBusyKind(null);
    }
  };

  const settleSave = async () => {
    try {
      const out = await pending.current;
      pending.current = null;
      if(!out) return;
      if(!out.ok){
        const m = String((out.e && out.e.message) || out.e || "");
        const s = m.includes("invalid") || m.includes("expired") || m.includes("400")
          ? "That code is wrong or expired. Go back one step and enter the six digits from the newest email."
          : m.includes("policy") || m.includes("password")
            ? "The new password does not meet the rules below."
            : m.includes("503") || /could not be saved|unavailable/i.test(m)
              ? "The password could not be saved. Wait a moment and try again."
              : "Check the code and password rules, then try again.";
        setErr({ t:"Password was not changed", s });
        return;
      }
      toast("Password changed · all sessions ended", "go");
      go("/auth/login");
    } finally {
      setBusyKind(null);
    }
  };

  if(!mode) return html`
    <${Shell}>
      <${Brand}/>
      <h1>Reset your password</h1>
      <p className="lede">Choose a recovery path that is available on this system. If a method is greyed out, it is turned off for this deployment.</p>
      ${emailOn===false && offlineOn===false
        ? html`<${Alert} tone="warn" title="No recovery path available"
            say="Email reset and offline recovery are both turned off. Contact an administrator."/>`
        : null}
      <button className="opt" disabled=${emailOn===false} onClick=${()=>{ setMode("email"); setStep(0); setErr(null); }}>
        <span style=${{ flex:"0 0 auto", width:32, height:32, borderRadius:"var(--r-sm)", display:"grid",
             placeItems:"center", background:"rgba(61,225,200,.12)", color:"var(--aqua)" }}>
          <${I} d="mail" w=${15}/></span>
        <span className="txt"><b>Email a six-digit code</b>
          <span>${emailOn===false
            ? "Email reset is turned off on this system."
            : emailOn===null
              ? "Checking availability…"
              : "We email six digits. Valid about fifteen minutes."}</span></span>
        <${I} d="arrowR" w=${15} s=${{ stroke:"var(--fg-3)", flex:"0 0 auto" }}/>
      </button>
      <button className="opt" disabled=${offlineOn===false} onClick=${()=>{ setMode("offline"); setErr(null); setBlob(""); setPlain(""); }}>
        <span style=${{ flex:"0 0 auto", width:32, height:32, borderRadius:"var(--r-sm)", display:"grid",
             placeItems:"center", background:"rgba(150,166,255,.14)", color:"var(--ai)" }}>
          <${I} d="key" w=${15}/></span>
        <span className="txt"><b>Offline recovery blob</b>
          <span>${offlineOn===false
            ? "Offline recovery is turned off on this system."
            : offlineOn===null
              ? "Checking availability…"
              : "An operator decrypts the blob with the private key."}</span></span>
        <${I} d="arrowR" w=${15} s=${{ stroke:"var(--fg-3)", flex:"0 0 auto" }}/>
      </button>
      <div className="auth__foot"><${FootLink} to="/auth/login">Back to sign in</${FootLink}></div>
    <//>`;

  if(mode === "offline") return html`
    <${Shell} wide>
      <${Brand}/>
      <h1>Offline recovery</h1>
      <p className="lede">We never learn whether that username exists. Unknown and disabled accounts receive a same-length decoy blob, so nothing here can be used to enumerate accounts.</p>
      ${err && html`<${Alert} title=${err.t} say=${err.s}/>`}
      <${Field} label="Username">
        <${Input} icon="user" value=${u} onChange=${e=>setU(e.target.value)} placeholder="your.name" name="username" autoComplete="username"/>
      <//>
      <div className="btn-row" style=${{ marginBottom:14 }}>
        <button className="btn btn--ghost btn--sm" disabled=${busy || !u.trim()} onClick=${async ()=>{
          setBusyKind("offline"); setErr(null);
          try {
            const out = await api(path("post__auth_forgot_start"), { method:"POST", body:{ username:u.trim() } });
            setBlob(String((out && out.recovery_blob_hex) || ""));
            toast("Recovery blob ready — hand it to the operator with the private key", "go");
          } catch (_e){
            setErr({ t:"Blob was not issued", s:"Please try again, or use email reset if available." });
          } finally { setBusyKind(null); }
        }}>${busy ? "Requesting…" : "Request encrypted blob"}</button>
      </div>
      <${Field} label="Encrypted blob" hint="Hand this to whoever holds the recovery private key. Valid for a limited time, single use.">
        <div className="flat" style=${{ padding:"13px 15px" }}>
          <code style=${{ fontSize:"var(--t-label)", lineHeight:1.7, wordBreak:"break-all", color:"var(--fg-3)" }}>
            ${blob || "— request a blob first —"}
          </code>
        </div>
      <//>
      <${Field} label="Decrypted payload" hint="Paste the whole single-line JSON that the offline recovery tool prints.">
        <${Input} icon="key" value=${plain} onChange=${e=>setPlain(e.target.value)}
          placeholder='{"jti":"…","u":"…","e":…,"s":"…"}'/>
      <//>
      <${Field} label="New password">
        <${Input} icon="lock" type=${show?"text":"password"} value=${pw} onChange=${e=>setPw(e.target.value)}
          placeholder="At least 12 characters" name="password" autoComplete="new-password"
          act=${html`<${I} d=${show?"eyeOff":"eye"} w=${15}/>`} onAct=${()=>setShow(s=>!s)}/>
      <//>
      <div className="pw-grid" style=${{ marginBottom:14 }}>
        ${rules.map(r=> html`<span key=${r.id} className="pw-rule" data-ok=${r.ok?"1":"0"}>
          <${I} d=${r.ok?"check":"minus"} w=${11}/>${r.say}</span>`)}
      </div>
      <${Field} label="Confirm"><${Input} icon="lock" type="password" value=${pw2} onChange=${e=>setPw2(e.target.value)} placeholder="Type it again" autoComplete="new-password"/><//>
      <${Alert} tone="info" title="Nothing is written until the payload verifies"
        say="The token is single-use. On success your session version is bumped, so every other device signs out immediately."/>
      <button className="btn btn--go btn--wide" disabled=${!ok || !plain.trim() || !u.trim() || busy}
        onClick=${async ()=>{
          setBusyKind("offline"); setErr(null);
          try {
            await api(path("post__auth_forgot_complete"), {
              method:"POST",
              body:{
                username:u.trim(),
                recovery_plain:plain.trim(),
                password:pw,
                password_confirm:pw2,
              },
            });
            toast("Password reset · all sessions ended", "go");
            go("/auth/login");
          } catch (_e){
            setErr({ t:"Password was not changed", s:"Check the decrypted payload and try again." });
          } finally { setBusyKind(null); }
        }}>Set a new password</button>
      <div className="auth__foot"><button type="button" onClick=${()=>setMode(null)}>Use a different method</button></div>
    <//>`;

  return html`
    <${Shell}>
      <${Brand}/>
      <${Steps} at=${step} items=${["Identify","Code","New password"]}/>
      ${err && html`<${Alert} title=${err.t} say=${err.s}/>`}
      <${AnimatePresence} mode="wait">
        ${step===0 && html`
          <${M.div} key="f0" initial=${{ opacity:0, x:12 }} animate=${{ opacity:1, x:0 }} exit=${{ opacity:0, x:-12 }} transition=${FADE}>
            <h1>Find your account</h1>
            <p className="lede">Both fields must match what is on file. Whatever you type, this screen answers the same way — that is deliberate. Username matching is not case-sensitive.</p>
            <${Field} label="Username"><${Input} icon="user" value=${u} onChange=${e=>setU(e.target.value)} placeholder="your.name" name="username" autoComplete="username"/><//>
            <${Field} label="Registered email"><${Input} icon="mail" type="email" value=${email} onChange=${e=>setEmail(e.target.value)} placeholder="you@company.com" name="email" autoComplete="email"/><//>
            <button className="btn btn--go btn--wide" disabled=${busy || !u.trim() || !email.trim()}
              onClick=${()=>{
                setErr(null);
                pending.current = api(path("post__auth_forgot_email_start"), {
                  method:"POST",
                  body:{ username:u.trim(), email:email.trim() },
                }).then(r=>({ ok:true, r })).catch(e=>({ ok:false, e }));
                setBusyKind("send");
              }}>${busyKind==="send" ? "Sending…" : "Send the code"}</button>
            <div className="auth__foot"><button type="button" onClick=${()=>setMode(null)}>Use a different method</button><${FootLink} to="/auth/login">Back to sign in</${FootLink}></div>
          <//>`}
        ${step===1 && html`
          <${M.div} key="f1" initial=${{ opacity:0, x:12 }} animate=${{ opacity:1, x:0 }} exit=${{ opacity:0, x:-12 }} transition=${FADE}>
            <h1>Enter the six-digit code</h1>
            <p className="lede">Use the six digits from your email (not letters). Valid about fifteen minutes. You can ask for another after a short wait.</p>
            <${OTP} len=${6} onDone=${c=>{ setCode(c); setStep(2); }}/>
            <div className="auth__foot"><button type="button" disabled=${busy} onClick=${async ()=>{
              setBusyKind("resend"); setErr(null);
              try {
                await api(path("post__auth_forgot_email_start"), {
                  method:"POST",
                  body:{ username:u.trim(), email:email.trim() },
                });
                toast("A new code was sent","warn");
              } catch(e){
                const m = String((e && e.message) || "");
                toast(m.includes("cooldown") || m.includes("429") ? "Please wait before requesting another code." : "Could not resend the code", "bad");
              } finally { setBusyKind(null); }
            }}>Resend code</button>
              <button type="button" onClick=${()=>setStep(0)}>Change email</button></div>
          <//>`}
        ${step===2 && html`
          <${M.div} key="f2" initial=${{ opacity:0, x:12 }} animate=${{ opacity:1, x:0 }} exit=${{ opacity:0, x:-12 }} transition=${FADE}>
            <h1>Choose a new password</h1>
            <p className="lede">Setting this ends every other session you have open, on every device.</p>
            <${Field} label="New password">
              <${Input} icon="lock" type=${show?"text":"password"} value=${pw} onChange=${e=>setPw(e.target.value)}
                placeholder="At least 12 characters" name="password" autoComplete="new-password"
                act=${html`<${I} d=${show?"eyeOff":"eye"} w=${15}/>`} onAct=${()=>setShow(s=>!s)}/>
            <//>
            <div className="pw-grid" style=${{ marginBottom:14 }}>
              ${rules.map(r=> html`<span key=${r.id} className="pw-rule" data-ok=${r.ok?"1":"0"}>
                <${I} d=${r.ok?"check":"minus"} w=${11}/>${r.say}</span>`)}
            </div>
            <${Field} label="Confirm"><${Input} icon="lock" type="password" value=${pw2} onChange=${e=>setPw2(e.target.value)} placeholder="Type it again" autoComplete="new-password"/><//>
            <button className="btn btn--go btn--wide" disabled=${!ok || !code || String(code).length !== 6 || busy}
              onClick=${()=>{
                setErr(null);
                pending.current = api(path("post__auth_forgot_email_complete"), {
                  method:"POST",
                  body:{
                    username:u.trim(),
                    email:email.trim(),
                    sha_code:String(code||"").replace(/\D/g,""),
                    password:pw,
                    password_confirm:pw2,
                  },
                }).then(r=>({ ok:true, r })).catch(e=>({ ok:false, e }));
                setBusyKind("save");
              }}>${busyKind==="save" ? "Saving…" : "Save and sign out everywhere"}</button>
          <//>`}
      <//>
      <${Overlay} show=${busyKind==="send"} title="Sending the reset code"
        say="Checking the account and handing a six-digit code to the mail system."
        steps=${FORGOT_SEND} onDone=${settleSend}/>
      <${Overlay} show=${busyKind==="save"} title="Saving your new password"
        say="Verifying the code, writing the password, and ending other sessions."
        steps=${FORGOT_SAVE} onDone=${settleSave}/>
    <//>`;
}

/* ═══════════════════ SIGN OUT ════════════════════════════════════════════
   Live POST /auth/logout clears the server session cookie. The UI also discards
   local me/CSRF. Soft “signed out locally only” invent is deleted.            */
export function SignOut({ toast }){
  const { me, signOut } = useSession();
  const [state,setState] = useState("ask");   // ask · gone
  const [busy,setBusy] = useState(false);

  useEffect(()=>{
    if(state !== "gone") return;
    const id = setTimeout(()=> go("/auth/login"), 4200);
    return ()=> clearTimeout(id);
  },[state]);

  if(state === "gone") return html`
    <${Shell}>
      <${Brand}/>
      <${M.div} className="out__art" initial=${{ opacity:0, scale:.94 }} animate=${{ opacity:1, scale:1 }}
          transition=${SOFT}>
        <div className="out__ring"><i/><i/></div>
      <//>
      <h1 className="auth__h" style=${{ textAlign:"center" }}>You are signed out</h1>
      <p className="auth__sub" style=${{ textAlign:"center" }}>
        This browser session was cleared on the server. Other devices stay signed in until their cookies expire or you change the password.</p>
      <button type="button" className="btn btn--go btn--wide" onClick=${()=>go("/auth/login")}>Sign in again</button>
      <div className="auth__foot"><span>Returning to sign-in shortly.</span></div>
    <//>`;

  return html`
    <${Shell}>
      <${Brand}/>
      <h1 className="auth__h">Sign out of Sentinel?</h1>
      <p className="auth__sub">You are signed in as <code>${me ? me.username : "—"}</code>${me && me.role ? html` · ${me.role}` : ""}.</p>

      <div className="out__rows">
        ${[["Alarms keep arriving","Signing out of the console does not stop phone or email alerts."],
           ["Open dispatches stay yours","Anything assigned to you remains assigned. Stand down first if you are leaving the shift."],
           ["Your position stops being read","If you are on call, turn it off — consent alone keeps you in the ranking pool."]].map(([t,s])=> html`
          <div key=${t} className="out__r"><${I} d="info" w=${14}/>
            <span><b>${t}</b><em>${s}</em></span></div>`)}
      </div>

      ${/* Ending every session is a password rotation — that is the only thing
            that moves the session stamp. Offering it as a switch here would
            promise something this screen cannot do.                        */""}
      <div className="out__opt">
        <button className="opt" onClick=${()=>go("/settings/security")} type="button">
          <span className="txt"><b>Need to eject every device?</b>
            <span>Change your password. That ends every signed-in device at once, including the mobile app. Signing out here only ends this browser.</span></span>
          <${I} d="chevron" w=${15}/>
        </button>
      </div>

      <div className="btn-row" style=${{ marginTop:18 }}>
        <button className="btn btn--go btn--wide" disabled=${busy}
          onClick=${async ()=>{
            setBusy(true);
            try {
              await signOut({ redirect:false });
            } catch (_) { /* local clear still happened inside signOut */ }
            setState("gone");
            setBusy(false);
            toast("Signed out");
          }}>${busy ? "Signing out…" : "Sign out"}</button>
      </div>
      <button type="button" className="btn btn--text btn--wide" onClick=${()=>history.back()}>Stay signed in</button>

      <div className="auth__foot">
        <span>A plain sign-out only ends this browser. Left open, the session still expires on its own.</span>
      </div>
    <//>`;
}

